import React, { useMemo, useState, useEffect } from "react";

import { HelpIcon, MoreIcon, Typography } from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import PageMeta from "Components/PageMeta";
import * as Dropdown from "ds/Dropdown";
import Loading from "ds/PageLoading";
import * as Sticker from "ds/Sticker";
import * as Table from "ds/Table";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { getSubscriptionEditUrl, capitalize } from "Libs/utils";
import { useLoadAccesses } from "Reducers/access/access.hooks";
import { environmentsAsArraySelector } from "Reducers/environment";
import { selectAddDomainSupportEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import { useLoadDomain } from "Reducers/project/settings/domains/hooks/useLoadDomain";
import { useLoadDomains } from "Reducers/project/settings/domains/hooks/useLoadDomains";
import { subscriptionSelector } from "Reducers/subscription";
import ConfigureDomainInfo from "src/organization/pages/project/environment/settings/domains/components/ConfigureDomainInfo/ConfigureDomainInfo";
import ModalConfirmDelete from "src/organization/pages/project/settings/domains/components/DeleteModal";
import DomainEmpty from "src/organization/pages/project/settings/domains/components/DomainEmpty";
import { useAppSelector } from "Store/hooks";

import * as S from "./DomainsList.styles";

import type { Project, Domain } from "@packages/client";

interface DomainListProps {
  project?: Project;
}
const DomainsList = ({ project }: DomainListProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const enableAddDomainSupportLink = useAppSelector(
    selectAddDomainSupportEnabled
  );

  const [modal, setModal] = useState<{
    isOpen: boolean;
    type?: string;
  }>({
    isOpen: false
  });

  const [domainForDelete, setDomainForDelete] = useState<Domain | null>(null);

  const { organizationId, projectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();

  const [sort, setSort] = Table.useSort<{ name: string; created_at: string }>({
    key: "created_at",
    direction: "asc"
  });

  const encodeDomainId = id => {
    return id.replace(/\./g, "");
  };

  const subscription = useSelectorWithUrlParams(subscriptionSelector, {
    id: project?.getSubscriptionId()
  });

  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);

  const { domains, loading } = useLoadDomains(project);

  const { status, handleDelete, hasDomainPermission } = useLoadDomain(project);

  const subscriptionEditUrl = useMemo(
    () => getSubscriptionEditUrl({ project, subscription }),
    [project, subscription]
  );
  const newDomainUrl = useMemo(
    () => `/${organizationId}/${projectId}/-/settings/domains/-/new`,
    [projectId, organizationId]
  );

  const isDevelopment = useMemo(
    () => project?.subscription?.plan === "development",
    [project]
  );

  const capabilites = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );

  const mainEnvironment = useMemo(
    () => environments.find(env => env.is_main),
    [environments]
  );
  const hasDomains = useMemo(() => domains && domains.length > 0, [domains]);
  const sortDomains = useMemo(() => {
    if (!domains) return [];
    const key = sort.key || "name";
    const list = domains.sort((a, b) => a[key].localeCompare(b[key]));
    if (sort.direction === "desc") {
      return list?.reverse();
    }
    return list;
  }, [domains, sort]);

  const viewDomain = domain => {
    navigate(
      `/${organizationId}/${projectId}/-/settings/domains/${encodeDomainId(
        domain.id
      )}`
    );
  };

  const customDomainEnabled = capabilites?.custom_domains?.enabled;

  const loadingAccesses = useLoadAccesses();

  const listDomainsUrl = useMemo(
    () => `/${organizationId}/${projectId}/-/settings/domains`,
    [organizationId, projectId]
  );

  useEffect(() => {
    if (status && ["deleted"].includes(status)) navigate(listDomainsUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const isDedicated = subscription?.enterprise_tag !== "";

  const supportAndDedicated = enableAddDomainSupportLink && isDedicated;

  const promtUpgradeText = () => {
    if (supportAndDedicated) {
      return "settings.domains.prompt_support";
    } else if (subscriptionEditUrl) {
      return "settings.domains.prompt_upgrade.plan_owner";
    } else {
      return "settings.domains.prompt_upgrade";
    }
  };

  if (loadingAccesses || loading) {
    return <Loading />;
  }

  return (
    <div>
      <PageMeta title={`Domains | ${project?.title}`} />

      {hasDomains && (
        <S.Add
          to={newDomainUrl}
          analyticId="settings.domains.add.button"
          disabled={!hasDomainPermission}
        >
          {intl.formatMessage({
            id: "settings.domains.add.button"
          })}
        </S.Add>
      )}

      {hasDomains && (
        <>
          <Typography.Title tag="h2" style={{ marginBottom: "16px" }}>
            {intl.formatMessage({ id: "domains.production_domains" })}
          </Typography.Title>

          <S.Info>
            {intl.formatMessage({
              id: "settings.domains.info"
            })}
          </S.Info>

          <S.Help>
            <HelpIcon />
            {intl.formatMessage<React.ReactNode>(
              { id: "settings.domains.learn_more" },
              {
                link: chunks => (
                  <S.Link
                    analyticId="links.documentation.domain"
                    href={intl.formatMessage({
                      id: "links.documentation.domain"
                    })}
                  >
                    {chunks}
                  </S.Link>
                )
              }
            )}
          </S.Help>
        </>
      )}

      {isDevelopment ? (
        <></>
      ) : hasDomainPermission ? (
        <ConfigureDomainInfo
          closed={hasDomains ? true : false}
          hostname={mainEnvironment?.edge_hostname}
        />
      ) : (
        <>
          <S.StickerRoot priority="information">
            <Sticker.Icon priority="information" />
            <Sticker.Text textStyle="information">
              {intl.formatMessage({
                id: "settings.domains.sticker.no_permissions"
              })}
            </Sticker.Text>
          </S.StickerRoot>

          <ConfigureDomainInfo
            closed={hasDomains ? true : false}
            hostname={mainEnvironment?.edge_hostname}
          />
        </>
      )}

      {isDevelopment ? (
        <>
          <Sticker.Root priority="information">
            <Sticker.Icon priority="information" />
            <Sticker.Text>
              {intl.formatMessage({
                id: promtUpgradeText()
              })}
            </Sticker.Text>
            {subscriptionEditUrl && !supportAndDedicated && (
              <>
                {subscriptionEditUrl.external ? (
                  <S.ButtonLink
                    analyticId="settings.domains.upgrade_plan"
                    href={subscriptionEditUrl.url}
                  >
                    {intl.formatMessage({
                      id: "settings.domains.upgrade_plan"
                    })}
                  </S.ButtonLink>
                ) : (
                  <S.ButtonLink
                    to={subscriptionEditUrl.url}
                    analyticId="settings.domains.upgrade_plan"
                  >
                    {intl.formatMessage({
                      id: "settings.domains.upgrade_plan"
                    })}
                  </S.ButtonLink>
                )}
              </>
            )}
          </Sticker.Root>

          <DomainEmpty
            disableAddButton={false}
            supportAndDedicated={supportAndDedicated}
          />
        </>
      ) : (
        <section aria-labelledby="settings-heading">
          {!domains && loading ? (
            <Loading />
          ) : (
            <>
              {hasDomains ? (
                <Table.Table>
                  <Table.Thead>
                    <Table.Th
                      width="80%"
                      onSortChanged={() => setSort("name")}
                      isSortActive={sort.key === "name"}
                      sortDirection={sort.direction}
                    >
                      {intl.formatMessage({ id: "settings.domains.name" })}
                    </Table.Th>
                    <Table.Th
                      width="20%"
                      onSortChanged={() => setSort("created_at")}
                      isSortActive={sort.key === "created_at"}
                      sortDirection={sort.direction}
                    >
                      {intl.formatMessage({ id: "settings.domains.date" })}
                    </Table.Th>
                  </Table.Thead>
                  <Table.Tbody>
                    {sortDomains.map(domain => (
                      <Table.Tr key={`domain-${domain.id}`}>
                        <Table.Td width="80%">
                          <S.Domain
                            data-testid={`domain-${encodeDomainId(domain.id)}`}
                          >
                            {domain.name}
                          </S.Domain>
                          {project?.default_domain === domain?.name && (
                            <S.Default>
                              {intl.formatMessage({
                                id: "settings.domains.default"
                              })}
                            </S.Default>
                          )}
                        </Table.Td>
                        <Table.Td width="17%">
                          <S.Date>
                            {moment(domain.created_at).format("MMMM D, YYYY")}
                          </S.Date>
                        </Table.Td>

                        <Table.Td width="3%">
                          <div
                            data-testid={`domain-${encodeDomainId(
                              domain.id
                            )}-btn`}
                          >
                            <Dropdown.Root id="domains_list">
                              <Dropdown.Trigger>
                                <MoreIcon />
                              </Dropdown.Trigger>
                              <Dropdown.Portal>
                                <Dropdown.Content align="end">
                                  <Dropdown.Item
                                    onClick={() => {
                                      viewDomain(domain);
                                    }}
                                  >
                                    {capitalize(
                                      intl.formatMessage({ id: "view" })
                                    )}
                                  </Dropdown.Item>

                                  {hasDomainPermission && (
                                    <Dropdown.Item
                                      data-testid="delete-domain-btn"
                                      onClick={() => {
                                        setDomainForDelete(domain);
                                        setModal({
                                          isOpen: true,
                                          type: "delete"
                                        });
                                      }}
                                    >
                                      {capitalize(
                                        intl.formatMessage({ id: "delete" })
                                      )}
                                    </Dropdown.Item>
                                  )}
                                </Dropdown.Content>
                              </Dropdown.Portal>
                            </Dropdown.Root>
                          </div>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table.Table>
              ) : (
                <>
                  {hasDomainPermission ? (
                    <DomainEmpty
                      disableAddButton={hasDomainPermission}
                      supportAndDedicated={supportAndDedicated}
                    />
                  ) : null}
                </>
              )}

              {modal.isOpen && domainForDelete && (
                <ModalConfirmDelete
                  closeModal={() => setModal({ isOpen: false })}
                  deleteProductionDomain={handleDelete}
                  domain={domainForDelete}
                  isOpen={modal.isOpen}
                  capabilityCustomDomains={customDomainEnabled}
                />
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default DomainsList;
